import { Injectable } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { forEach } from 'lodash-es';
import { WebsocketService } from './websocket.service';
import { WebsocketChannelHandle } from '../models/websocket-channel-handle.model';

export type ModelEventEnvelope<T> = [modelType: string, modelId: number, data: T];

export class ModelEventsSubscriptionSet<T> {
  private streams: Record<string, WebsocketChannelHandle> = {};
  private messageSubscriptions: Record<string, Subscription> = {};
  public events: Subject<ModelEventEnvelope<T>> = new Subject<ModelEventEnvelope<T>>();

  constructor(
    private websocketService: WebsocketService
  ) { }

  subscribe(modelName: string, modelId: number) {
    const streamName = this.makeStreamName(modelName, modelId)

    if (!this.streams[streamName]) {
      const channel = this.websocketService.channelHandle("ModelEventsChannel", { modelName, modelId });
      this.streams[streamName] = channel;

      const messageReceivedObservable = this.streams[streamName].messageReceived;
      const messageSubscription = messageReceivedObservable.subscribe(this.handleMessage(modelName, modelId));
      this.messageSubscriptions[streamName] = messageSubscription;
    }

    return this.messageSubscriptions[streamName];
  }

  unsubscribe(modelName: string, modelId: number): void {
    const streamName = this.makeStreamName(modelName, modelId);
    this.streams[streamName]?.unsubscribe();
    this.messageSubscriptions[streamName].unsubscribe();
    delete this.streams[streamName];
  }

  unsubscribeAll(): void {
    forEach(this.streams, (channel: WebsocketChannelHandle) => channel.unsubscribe());
    this.streams = {};
  }

  private makeStreamName(modelType: string, modelId: number): string {
    return `${modelType}:${modelId}`;
  }

  private handleMessage(modelName: string, modelId: number) {
    return (data: T) => {
      const event: ModelEventEnvelope<T> = [modelName, modelId, data];
      this.events.next(event);
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class ModelEventsService {
  constructor(
    private websocketService: WebsocketService
  ) { }

  newSet<T>() {
    return new ModelEventsSubscriptionSet<T>(this.websocketService);
  }

}
